import { Suspense, lazy } from 'react'
import { Routes, Route, Navigate, NavLink, useLocation } from 'react-router-dom'
import { useAuth } from './contexts/AuthContext.jsx'
import UserMenu from './components/UserMenu.jsx'
import BrandRule from './components/BrandRule.jsx'
import Login from './pages/Login.jsx'
import MySchedule from './pages/MySchedule.jsx'
import Board from './pages/Board.jsx'
import People from './pages/People.jsx'
import Whiteboard from './pages/Whiteboard.jsx'
import Tv from './pages/Tv.jsx'
import Settings from './pages/Settings.jsx'
import Connect from './pages/Connect.jsx'
import NavBoard from './components/NavBoard.jsx'
import NavMore from './components/NavMore.jsx'
import ListsIndex from './pages/ListsIndex.jsx'
import ListBoard from './pages/ListBoard.jsx'
import { useRealtime } from './lib/useRealtime.js'
import Icon from './components/Icon.jsx'

// The only page loaded on demand. Help carries the markdown renderer and the whole changelog,
// neither of which anybody filling a board needs, and it is opened a few times a year rather
// than every morning — so it is the one page where a moment's wait on first open is the
// right trade for a lighter board.
const Help = lazy(() => import('./pages/Help.jsx'))

/**
 * The wall view answers to both addresses.
 *
 * /wall-view is the one the nav links to and the install guide now gives. /whiteboard is what
 * every TV already mounted on a wall is pointed at — those are set once, with a remote and a
 * ladder — so it keeps working rather than becoming a sign-in screen on the next reboot. A
 * paired display is not `signedIn` (it holds a display cookie, not a user session), so it is
 * this list, not the routes below, that decides what a screen at the old address sees.
 */
const WALL_PATHS = ['/wall-view', '/whiteboard']

/**
 * The nav carries only what a given person opens during a working day, in the order they
 * open it: the board they fill, the lists they keep beside it, and the people on both.
 * Everything visited less than weekly — Settings, and the wall view, which is a preview of
 * a screen that runs by itself — lives in the user menu instead.
 *
 *   field staff     My schedule  (+ Board and Directory if their visibility allows it)
 *   PM / PMA        Board, Lists, Directory; My schedule only if also field staff
 *   coordinator     same as PM, in CAT mode
 *   admin / owner   Board, Lists, Directory
 *
 * No mode badge here on purpose: which board is live is already unmistakable the moment you
 * open it (CAT teams and colours look nothing like PM rows), and repeating it as a pill in
 * the header just for a company that has never once run CAT mode is a permanent fixture that
 * earns its keep on a handful of days a year.
 */
function TopBar() {
  const { can, tenant, listsEnabled } = useAuth()

  return (
    <header className="topbar">
      {tenant?.logoPath && <img className="logo" src={`/uploads/${tenant.logoPath}`} alt="" />}
      <span className="tenant-name">{tenant?.name}</span>

      {/* A hairline between whose board this is and what can be opened in it. They are
          different kinds of thing — an identity and a set of destinations — and without it
          the company name reads as the first item in the nav. */}
      <span className="topbar-divider" aria-hidden="true" />

      <nav>
        {can.isFieldStaff && <NavLink to="/" end>My schedule</NavLink>}
        {/* One item for every board: the schedule, and each list kept beside it, picked
            between rather than navigated to separately. See NavBoard — with no lists it is
            simply the link to the board that it always was. */}
        {can.seeBoard && <NavBoard listsOn={listsEnabled()} />}
        {/* A reader whose visibility keeps them off the board can still read a list, and
            for them the picker above never renders — so the lists need their own way in. */}
        {!can.seeBoard && listsEnabled() && <NavLink to="/lists">Lists</NavLink>}
        {/* The standing picture of who works here, as against the board's picture of one
            day. Last, because it is the reference you consult rather than the thing you
            fill. Same audience as the board — it shows the same people, filtered the same. */}
        {can.seeBoard && <NavLink to="/directory">Directory</NavLink>}
        {/* Everywhere else you can go: the wall view, and Settings. Both are destinations
            rather than account matters, so they belong in the nav — one click down, which
            is the depth they always wanted, rather than under the avatar. */}
        <NavMore />
      </nav>

      {/* Help, and the one way into it. Not a nav item: the nav is where work happens, and
          documentation about it is the wrong weight to sit beside the board as a peer. */}
      <NavLink
        to="/help"
        className={({ isActive }) => `help-link ${isActive ? 'active' : ''}`}
        title="Help"
        aria-label="Help"
      >
        <Icon name="help" size={19} />
      </NavLink>

      <UserMenu />
    </header>
  )
}

/** A page that has changed address, carrying the query string across with it. */
function RenamedTo({ to }) {
  const { search } = useLocation()
  return <Navigate to={`${to}${search}`} replace />
}

export default function App() {
  const { loading, signedIn, can, listsEnabled } = useAuth()
  const location = useLocation()

  // Above the early returns below, because hooks cannot follow a conditional return — which
  // is why this takes a flag rather than being called conditionally. A paired display never
  // reaches `signedIn`, so wall TVs keep their poll and never open a stream.
  useRealtime(signedIn)

  // The TV pairing screen has no session by definition — that is what it is for.
  if (location.pathname === '/tv') return <Tv />
  // A paired display goes straight to the wall view without ever meeting the app chrome —
  // at either of its addresses, so a screen still pointed at the old one keeps working. See
  // WALL_PATHS.
  if (WALL_PATHS.includes(location.pathname) && !signedIn && !loading) return <Whiteboard />

  if (loading) return <div className="empty">Loading…</div>
  if (!signedIn) return <Login />

  // The OAuth consent screen, outside the chrome like the wall views above it. It is a page
  // rather than a server-rendered form so that signing in is already solved — the guard
  // immediately above renders <Login/> in place at this URL, query string intact — and it
  // gets no nav bar because a person part-way through granting access to their account
  // should be deciding that, not being offered four ways to wander off.
  if (location.pathname === '/connect') return <Connect />

  // Field staff land on their own schedule; everyone else on the board they work from.
  const home = can.isFieldStaff ? <MySchedule /> : <Navigate to="/board" replace />

  return (
    <div className="app">
      <BrandRule />
      <TopBar />
      <Routes>
        <Route path="/" element={home} />
        <Route path="/board" element={<Board />} />
        <Route path="/directory" element={<People />} />
        {/* Was /people. The query string travels with it: that page keeps its filters there,
            so dropping the search would land somebody on an unfiltered Directory rather than
            the one they had bookmarked. */}
        <Route path="/people" element={<RenamedTo to="/directory" />} />
        {/* Behind the same switch as the nav item, so a typed URL cannot reach a list on a
            company that has them off — the server refuses too, but a redirect reads better
            than an error card. */}
        <Route path="/lists" element={listsEnabled() ? <ListsIndex /> : <Navigate to="/" replace />} />
        <Route path="/lists/:slug" element={listsEnabled() ? <ListBoard /> : <Navigate to="/" replace />} />
        <Route path="/wall-view" element={<Whiteboard />} />
        {/* Was /whiteboard. Only a signed-in PERSON reaches this and gets moved to the new
            address; a wall TV is served in place by the guard above and never redirects. */}
        <Route path="/whiteboard" element={<RenamedTo to="/wall-view" />} />
        <Route path="/settings/*" element={<Settings />} />
        {/* Open to everybody signed in, field staff included: the guide reads no company
            data, and About is exactly what somebody needs to write a useful support email. */}
        <Route
          path="/help/*"
          element={<Suspense fallback={<div className="empty">Loading…</div>}><Help /></Suspense>}
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  )
}
